import React, { useState, useEffect } from "react";
import { FileText, Calendar, CheckCircle, Edit2, Save, X } from "lucide-react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const IndentDetails2 = ({ row, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isTypeEditing, setIsTypeEditing] = useState(false);
  const [isTaskScheduleEditing, setIsTaskScheduleEditing] = useState(false);
  const [poDetails, setPoDetails] = useState(row?.podetails || "");
  const [selectedType, setSelectedType] = useState(row?.TypeName || "");
  const [taskSchedule, setTaskSchedule] = useState(row?.TaskSchedule || "");

  // Update local state when row prop changes
  useEffect(() => {
    setPoDetails(row?.podetails || "");
    setSelectedType(row?.TypeName || "");
    setTaskSchedule(row?.TaskSchedule || "");
  }, [row]);
  const [indentTypes, setIndentTypes] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isTypeEditing) {
      fetchIndentTypes();
    }
  }, [isTypeEditing]);

  const fetchIndentTypes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/dropdown/mindenttypes`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setIndentTypes(response.data);
    } catch (err) {
      console.error("Error fetching indent types:", err);
      setError("Failed to load indent types");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    try {
      // Handle DD/MM/YY format
      if (typeof dateString === 'string' && dateString.includes('/')) {
        const parts = dateString.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
          const year = parseInt(parts[2], 10);
          
          // Handle 2-digit years (assuming 20xx for years < 50, 19xx for years >= 50)
          const fullYear = year < 50 ? 2000 + year : 1900 + year;
          
          const date = new Date(fullYear, month, day);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: '2-digit'
            });
          }
        }
      }
      
      // Try standard date parsing
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return dateString;
      }
      
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  if (!row) {
    return (
      <div className="text-center text-gray-500 p-4">
        No indent details available
      </div>
    );
  }

  const displayData = {
    indentId: String(row.IndentID || ""),
    status: String(row.Status || ""),
    creationDate: formatDate(row.CreationDate),
    projectName: String(row.ProjectName || ""),
    typeName: String(row.TypeName || ""),
    taskSchedule: String(row.TaskSchedule || ""),
  };

  const isEmergency = displayData.typeName.toLowerCase() === "emergency";
  const canEdit = row.L2 === 1 || row.L2 === "Approved";
  
  // More flexible editing permissions - allow editing for various L1/L2 combinations
  const canEdit2 = 
    (row.L1 === 1 && row.L2 === 0) ||  // L1 approved, L2 pending
    (row.L1 === 1 && row.L2 === 1) ||  // L1 approved, L2 approved
    (row.L1 === "1" && row.L2 === "0") ||  // String values
    (row.L1 === "1" && row.L2 === "1") ||  // String values
    (row.L1 === 1 && row.L2 === "0") ||    // Mixed types
    (row.L1 === 1 && row.L2 === "1") ||    // Mixed types
    (row.L1 === "1" && row.L2 === 0) ||    // Mixed types
    (row.L1 === "1" && row.L2 === 1) ||    // Mixed types
    (row.L1 === 1) ||                      // Fallback: if L1 is approved, allow editing
    (row.L1 === "1");                      // Fallback: if L1 is approved (string), allow editing
  

  const handleSave = async () => {
    try {
      const response = await axios.put(`${API_BASE_URL}/popage/indent/${row.IndentID}/podetails`, {
        podetails: poDetails
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setIsEditing(false);
      if (onUpdate) {
        onUpdate({ ...row, podetails: poDetails });
      }
      setError("");
    } catch (err) {
      setError("Failed to update PO details");
      console.error("Error updating PO details:", err);
    }
  };

  const handleTypeUpdate = async () => {
    try {
      const response = await axios.put(`${API_BASE_URL}/l2page/indent/${row.IndentID}/l1type`, {
        typeName: selectedType
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setIsTypeEditing(false);
      if (onUpdate) {
        onUpdate({ ...row, TypeName: selectedType });
      }
      setError("");
    } catch (err) {
      setError("Failed to update indent type");
      console.error("Error updating indent type:", err);
    }
  };

  const handleTaskScheduleUpdate = async () => {
    try {
      const response = await axios.put(`${API_BASE_URL}/l2page/indent/${row.IndentID}/taskschedule`, {
        taskSchedule: taskSchedule
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setIsTaskScheduleEditing(false);
      if (onUpdate) {
        onUpdate({ ...row, TaskSchedule: taskSchedule });
      }
      setError("");
    } catch (err) {
      setError("Failed to update task schedule");
      console.error("Error updating task schedule:", err);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setPoDetails(row?.podetails || "");
    setError("");
  };

  const handleCancelTypeEdit = () => {
    setIsTypeEditing(false);
    setSelectedType(row?.TypeName || "");
    setError("");
  };

  const handleCancelTaskScheduleEdit = () => {
    setIsTaskScheduleEditing(false);
    setTaskSchedule(row?.TaskSchedule || "");
    setError("");
  };

  return (
    <div className="container mx-auto p-4 bg-white shadow-lg rounded-lg">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 p-3 rounded-lg">
          <h2 className={`text-xl font-semibold mb-2 ${isEmergency ? 'text-red-700' : 'text-gray-700'} border-b pb-1`}>
            <FileText className={`inline-block mr-1 ${isEmergency ? 'text-red-500' : 'text-blue-500'}`} size={20} />
            Indent #{displayData.indentId}
          </h2>
          <div className={`flex justify-between text-sm md:text-l ${displayData.status === "Draft" ? "text-yellow-600" :
              displayData.status === "Completed" ? "text-green-600" : "text-gray-600"
            }`}>
            <span className={`font-medium ${isEmergency ? 'text-red-600' : 'text-gray-600'}`}>Status:</span>
            <span className={isEmergency ? 'text-red-600' : ''}>
              {displayData.status}
            </span>
          </div>
          <div className="flex justify-between text-sm md:text-l mt-1">
            <span className={`font-medium ${isEmergency ? 'text-red-600' : 'text-gray-600'}`}>Creation Date:</span>
            <span className={isEmergency ? 'text-red-600' : ''}>
              {displayData.creationDate}
            </span>
          </div>
          <div className="flex justify-between items-center text-sm md:text-l mt-1">
            <span className={`font-medium ${isEmergency ? 'text-red-600' : 'text-gray-600'}`}>PO Details:</span>
            {isEditing ? (
              <div className="flex items-center gap-2 flex-1 ml-2">
                <input
                  type="text"
                  value={poDetails}
                  onChange={(e) => setPoDetails(e.target.value)}
                  className="flex-1 p-1 border rounded"
                />
                <button
                  onClick={handleSave}
                  className="p-1 bg-green-500 text-white rounded hover:bg-green-600"
                  title="Save"
                >
                  <Save size={16} />
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
                  title="Cancel"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className={isEmergency ? 'text-red-600' : ''}>
                  {poDetails}
                </span>
                {canEdit && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="p-1 text-blue-500 hover:text-blue-600"
                    title="Edit PO Details"
                  >
                    <Edit2 size={16} />
                  </button>
                )}
              </div>
            )}
          </div>
          {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        </div>


        <div className="bg-gray-50 p-3 rounded-lg">
          <h2 className={`text-xl font-semibold mb-2 ${isEmergency ? 'text-red-700' : 'text-gray-700'} border-b pb-1`}>
            <CheckCircle className={`inline-block mr-1 ${isEmergency ? 'text-red-500' : 'text-green-500'}`} size={20} />
            Project Information
          </h2>
          <div className="flex justify-between text-sm md:text-l">
            <span className={`font-medium ${isEmergency ? 'text-red-600' : 'text-gray-600'}`}>Project Name:</span>
            <span className={isEmergency ? 'text-red-600' : ''}>
              {displayData.projectName}
            </span>
          </div>
          <div className="flex justify-between text-sm md:text-l mt-1">
            <span className={`font-medium ${isEmergency ? 'text-red-600' : 'text-gray-600'}`}>Indent Type:</span>
            {isTypeEditing && canEdit2 ? (
              <div className="flex items-center gap-2">
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="p-1 border rounded flex-1"
                >
                  {indentTypes.map((type) => (
                    <option key={type.TypeID} value={type.TypeName}>
                      {type.TypeName}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleTypeUpdate}
                  className="p-1 bg-green-500 text-white rounded hover:bg-green-600"
                  title="Save"
                >
                  <Save size={16} />
                </button>
                <button
                  onClick={handleCancelTypeEdit}
                  className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
                  title="Cancel"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className={isEmergency ? 'text-red-600' : ''}>
                  {displayData.typeName}
                </span>
                {canEdit2 && (
                  <button
                    onClick={() => setIsTypeEditing(true)}
                    className="p-1 text-blue-500 hover:text-blue-600"
                    title="Edit Indent Type"
                  >
                    <Edit2 size={16} />
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-between text-sm md:text-l mt-1">
            <span className={`font-medium ${isEmergency ? 'text-red-600' : 'text-gray-600'}`}>Task Schedule:</span>
            {isTaskScheduleEditing && canEdit2 ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={taskSchedule}
                  onChange={(e) => setTaskSchedule(e.target.value)}
                  className="p-1 border rounded flex-1"
                  placeholder="Enter task schedule"
                />
                <button
                  onClick={handleTaskScheduleUpdate}
                  className="p-1 bg-green-500 text-white rounded hover:bg-green-600"
                  title="Save"
                >
                  <Save size={16} />
                </button>
                <button
                  onClick={handleCancelTaskScheduleEdit}
                  className="p-1 bg-red-500 text-white rounded hover:bg-red-600"
                  title="Cancel"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className={isEmergency ? 'text-red-600' : ''}>
                  {displayData.taskSchedule}
                </span>
                {canEdit2 && (
                  <button
                    onClick={() => setIsTaskScheduleEditing(true)}
                    className="p-1 text-blue-500 hover:text-blue-600"
                    title="Edit Task Schedule"
                  >
                    <Edit2 size={16} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndentDetails2;